import { ArrowElbowDownRightIcon } from '@phosphor-icons/react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { Building } from '@/reducers/types'

import { PlaceFeatureKey } from '../../features/places-features.constants'
import { hasFeatureDescription } from '../../features/places-features.descriptions'
import * as S from './place-feature-collapsible-group.styled'
import { PlaceFeatureNode } from './place-feature-tree'
import { usePlaceFeatureControl } from './use-place-feature-control'

interface PlaceFeatureCardRowProps {
  building: Building
  customLabels?: Partial<Record<PlaceFeatureKey, string>>
  errorFeatures: string[]
  errorMessage?: string
  isAdmin: boolean
  isChild?: boolean
  isEditing: boolean
  node: PlaceFeatureNode
}

const PlaceFeatureCardRow = ({
  building,
  customLabels,
  errorFeatures,
  errorMessage,
  isAdmin,
  isChild,
  isEditing,
  node,
}: PlaceFeatureCardRowProps) => {
  const { t } = useTranslation(['organisation'])
  const featureKey = node.definition.key as PlaceFeatureKey
  const error = errorFeatures.includes(featureKey) ? errorMessage : undefined

  const feature = usePlaceFeatureControl({
    building,
    customLabel: customLabels?.[featureKey],
    error,
    featureKey,
    isAdmin,
    isEditing,
  })

  if (!feature) return null

  const { controlWithReason, hint, label } = feature
  const description = hasFeatureDescription(featureKey)
    ? t(`organisation:places.features.descriptions.${featureKey}`)
    : undefined

  return (
    <>
      <S.Row $isChild={isChild}>
        {isChild && (
          <S.ChildBullet>
            <Icon icon={ArrowElbowDownRightIcon} size="1.2rem" />
          </S.ChildBullet>
        )}
        <S.RowLabel $isChild={isChild}>{label}</S.RowLabel>
        {(description || hint) && (
          <Tooltip>
            {description}
            {hint && <S.TooltipHint>{hint}</S.TooltipHint>}
          </Tooltip>
        )}
        {controlWithReason}
      </S.Row>
      {error && <S.RowMessage $error>{error}</S.RowMessage>}
    </>
  )
}

type PlaceFeatureCardProps = Omit<PlaceFeatureCardRowProps, 'isChild'>

const PlaceFeatureChildren = (props: PlaceFeatureCardProps) =>
  props.node.children.map((child) => (
    <Fragment key={child.definition.key}>
      <PlaceFeatureCardRow {...props} isChild node={child} />
      <PlaceFeatureChildren {...props} node={child} />
    </Fragment>
  ))

export const PlaceFeatureCard = (props: PlaceFeatureCardProps) => (
  <S.Card>
    <PlaceFeatureCardRow {...props} />
    {props.node.children.length > 0 && (
      <S.Children>
        <PlaceFeatureChildren {...props} />
      </S.Children>
    )}
  </S.Card>
)
